import { LocaleData } from '../../../models/locale/locale-data';
import { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'REGISTRATI ORA',
  heroCopy: 'Ottima scelta per il tuo acquisto!',
  heroSubCopy1:
    'Registra il tuo elettrodomestico entro 60 giorni e attiva subito i vantaggi gratuiti',
  benefitTicks: [
    '10 anni di servizi di riparazione con ricambi gratis*',
    'Ricambi originali Beko',
    'Protezione aggiuntiva opzionale per danni accidentali e guasti',
  ],
  heroDisclaimer:
    'Le parti includono motori, pompe, schede di controllo e altri componenti idonei.',
  registration: 'Chiama ora per registrati',
  ctaPhoneText: 'Contattaci al 800 694 566',
  phoneSubCopy:
    'Linee aperte dal lunedì al venerdì dalle 8:00 alle 20:00, il sabato dalle 8:00 alle 17:00.',
  onlineCopy: 'Bastano pochi minuti',
  ctaOnlineText: 'Registrati online',
  sixtyDayHeader: "Hai 60 giorni dall'acquisto",
  sixtyDayBody:
    'Completa la procedura, registrati entro la scadenza e avrai 10 anni di riparazioni e ricambi inclusi.',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP1 COPY V2_SEPT 2026 2.xlsx (BEKO tab)
// as applied in the approved mockups (claude-notes/lp-hybrid-lp1). Every line
// matches Beko control or V1. Dials the same number as V1: `ctaPhoneLink` is
// V1's (undefined = the base link). The `**` on the first heading is appended
// by PhoneOnlyV2.
const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Registra il tuo prodotto',
  offerHeadline: ['10 ANNI', 'di servizi di riparazione', 'CON RICAMBI GRATIS*'],
  sixtyDayHeader:
    "Registrazione necessaria entro 60 giorni dalla data di acquisto dell' elettrodomestico",
  registration: 'Chiama ora per registrarti',
  ctaPhoneText: 'Contattaci al numero gratuito\n800 694 566',
  ctaPhoneLink: v2Copy.ctaPhoneLink,
  benefitsHeader: 'Approfitta subito dei tuoi vantaggi gratuiti:',
  benefitTicks: [
    '10 anni di servizi di riparazione con ricambi gratis',
    'Ricambi originali Beko',
    'Protezione opzionale contro danni accidentali',
  ],
  phoneSubCopy:
    'Linee aperte dal lunedì al venerdì dalle 8:00 alle 20:00, il sabato dalle 8:00 alle 17:00',
  repeatHeading: 'Pronto a registrare?',
  ctaPhoneRepeatText: 'Contattaci ora al 800 694 566',
};

export const data: LocaleData = {
  banner: 'Registra il tuo prodotto per',
  heroNumber: '10',
  heroCopy: 'Anni di Servizi di Riparazione con Ricambi GRATIS*',
  heroSubCopy1: '<b>Registrazione necessaria</b>',
  heroSubCopy2: "entro 60 giorni dalla data di acquisto dell' elettrodomestico",
  registration: 'Chiama & registrati ora**',
  ctaPhoneText: '800 694 566',
  ctaPhoneLink: 'tel:800694566',
  phoneSubCopy:
    'La registrazione telefonica è rapida e ti fornisce informazioni aggiuntive sulla garanzia del produttore e sul servizio di 10 Anni di Servizi di Riparazione con Ricambi GRATIS*. Il servizio è aperto dal lunedì al venerdì dalle 8:00 alle 20:00 e il sabato dalle 8:00 alle 17:00.',
  // Matches production's trailing link ("<0>Clicca qui</0>."). Rendered via
  // parseText with ctaOnlineLink as link index 0.
  onlineCopy: 'In alternativa, puoi registrarti online. <0>Clicca qui</0>.',
  ctaOnlineText: 'Clicca qui',
  ctaOnlineLink: 'https://beko.register-appliance.com/RegistroPorMarca?country=IT&language=it',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Registrati online',
      linesClosedMessage:
        'Le nostre linee telefoniche sono attualmente chiuse, ma puoi registrarti online in qualsiasi momento',
      phoneNumberAsterix: [
        '** Servizi Domestic & General Italia S.r.l., nostro partner di servizi, registrerà il tuo elettrodomestico. Se sei interessato, Servizi Domestic & General Italia S.r.l. ti offrirà estensioni di assistenza per il tuo elettrodomestico. Per conoscere le modalità con cui Beko Italy S.r.l. e Servizi Domestic & General Italia S.r.l. trattano i tuoi dati, visita il sito <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
    // LP3 COPY V1_JULY 2026.xlsx (BEKO tab; ** added per master sheet).
    'link-only': {
      registration: 'Registra ora il tuo elettrodomestico per sbloccare i tuoi vantaggi gratuiti**',
      onlineCopy:
        'Le nostre linee telefoniche sono attualmente chiuse, ma puoi registrarti online in qualsiasi momento',
      ctaOnlineText: 'Registrati online',
      phoneNumberAsterix: [
        '** Servizi Domestic & General Italia S.r.l., nostro partner di servizi, registrerà il tuo elettrodomestico. Se sei interessato, Servizi Domestic & General Italia S.r.l. ti offrirà estensioni di assistenza per il tuo elettrodomestico. Per conoscere le modalità con cui Beko Italy S.r.l. e Servizi Domestic & General Italia S.r.l. trattano i tuoi dati, visita il sito <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
  },
  benefitsHeader: 'I benifici della registrazione:',
  benefits: [
    'Conservazione della tua prova di acquisto in formato digitale',
    'Aggiornamenti importanti sull’elettrodomestico e sulla sicurezza',
    'Riparazioni effettuate da centri assistenza autorizzati con ricambi originali',
  ],
  footerLinks: [
    { text: 'Termini e Condizioni', link: '/terms' },
    {
      text: 'Informativa Privacy',
      link: 'https://www.domesticandgeneral.com/beko/sicurezza-e-privacy',
    },
    { text: 'Cookies', link: '/cookies' },
  ],
  footerCopy:
    'Il sito è di proprietà di Domestic & General Group Limited e operato da Servizi Domestic & General Italia S.r.l., Via Gobetti 2/C, 20063, Cernusco sul Naviglio (MI).',
  heroAsterix: [
    '* Registra il tuo prodotto entro 60 giorni dalla data di acquisto dell’elettrodomestico e, dopo la scadenza della garanzia legale, in caso di guasto, per 10 anni dalla data di scadenza della garanzia legale hai la facoltà di acquistare servizi di riparazione a prezzo fisso forfettario che non prevedono il costo del ricambio. Servizio offerto da Beko Italy S.r.l. Il prezzo del servizio di riparazione è variabile nel tempo. Per conoscere il prezzo del servizio attualmente vigente chiamare il numero verde di Servizi Domestic & General Italia S.r.l. partner di Beko Italy S.r.l. per la fornitura di servizi di assistenza su elettrodomestici, o consultare la pagina web del sito <0>https://www.beko.com/it-it</0>. Servizio soggetto a termini e condizioni. Disponibile solo in Italia. Prima della registrazione leggere attentamente il dettaglio di termini e condizioni del servizio disponibile nel sito <0>https://www.beko.com/it-it</0>',
  ],
  heroAsterixLinks: ['https://www.beko.com/it-it'],
  phoneNumberAsterix: [
    '** Numero di telefono di Servizi Domestic & General Italia S.r.l., nostro partner di servizi. Se sei interessato, Servizi Domestic & General Italia S.r.l. ti offrirà estensioni di assistenza per il tuo elettrodomestico. Per conoscere le modalità con cui Beko Italy S.r.l. e Servizi Domestic & General Italia S.r.l. trattano i tuoi dati, visita il sito <0>https://www.domesticandgeneral.com/beko</0>',
  ],
  phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
  additionalSmallPrint: '',
  languageSwitcherLabel: 'Paese',
  languageSwitcherPlaceholder: 'Seleziona il tuo paese',
  languageList: [
    { label: 'Germania', value: 'de-DE' },
    { label: 'Francia', value: 'fr-FR' },
    { label: 'Italia', value: 'it-IT' },
    { label: 'Spagna', value: 'es-ES' },
    { label: 'Paesi Bassi', value: 'nl-NL' },
    { label: 'Belgio (francese)', value: 'be-FR' },
    { label: 'Belgio (olandese)', value: 'be-NL' },
  ],
  cookiePolicy: {
    title: 'Informativa sui cookie',
    sections: [
      {
        heading: 'Cosa sono i cookie?',
        description: [
          'I cookie sono piccoli file di testo che vengono inviati al tuo dispositivo quando visiti un sito web. Rimangono sul tuo dispositivo e vengono reinviati al sito web di provenienza ogni volta che lo visiti nuovamente. I cookie raccolgono dati su di te, come la tua preferenza linguistica o le tue informazioni di accesso. Questi cookie sono impostati da noi e sono chiamati cookie di prima parte. Utilizziamo anche cookie di terze parti, ovvero cookie provenienti da un dominio diverso da quello del sito web che stai visitando. Questi cookie possono aiutarci a migliorare le nostre attività pubblicitarie e di marketing. Utilizziamo cookie e altre tecnologie di tracciamento per i seguenti scopi:',
        ],
      },
      {
        heading: 'Gestione dei cookie',
        description: [
          "Per modificare le impostazioni dei cookie, utilizza il nostro strumento di gestione dei cookie oppure l'icona di sicurezza presente in ogni pagina. Puoi bloccare l'uso di alcuni o tutti i cookie e tecnologie simili che utilizziamo sul sito web cliccando sulle diverse categorie di cookie. Tuttavia, tieni presente che ciò potrebbe influire sulla tua esperienza sul sito web e sui servizi che possiamo offrire. Tieni inoltre presente che la cancellazione di tutti i cookie dal browser eliminerà tutti i cookie che memorizzano le tue preferenze.",
          "La maggior parte dei browser spiega come non accettare più nuovi cookie, come ricevere una notifica quando si riceve un nuovo cookie e come disabilitare completamente i cookie. Per eliminare i cookie esistenti precedentemente installati sul browser, è possibile selezionare l'opzione per cancellare la cronologia di navigazione e scegliere di eliminare o cancellare i cookie.",
          'Alcuni browser, come Chrome e Firefox, consentono di modificare le impostazioni per navigare in modalità "incognito", limitando la quantità di dati memorizzati sul computer ed eliminando automaticamente tutti i cookie persistenti presenti sul dispositivo al termine della sessione di navigazione.',
          "In alternativa, puoi visitare il sito <0>www.aboutcookies.org</0> , che contiene informazioni complete su come rimuovere i cookie su un'ampia gamma di browser.",
          'Per saperne di più sui cookie in generale e su come verificare quali terze parti hanno attualmente abilitato i cookie sul tuo dispositivo mobile e come disattivarne alcuni, visita <1>www.networkadvertising.org/mobile-choices</1>. Sul tuo dispositivo mobile puoi anche selezionare "Limita monitoraggio annunci" (sui dispositivi iOS) o "Disattiva annunci basati sugli interessi" (su Android), che ti consente di limitare l\'utilizzo delle informazioni sul tuo utilizzo delle app per la pubblicazione di annunci mirati ai tuoi interessi.',
        ],
        links: [
          'https://www.aboutcookies.org',
          'https://www.networkadvertising.org/mobile-choices',
        ],
      },
      {
        heading: 'Siti web di terze parti',
        description: [
          "Il sito web potrebbe contenere link o essere collegato ad altri siti web. Cliccando sul sito web collegato, abbandonerete il sito web e i contenuti da noi controllati e sarete collegati al sito web di terze parti. Non abbiamo alcun controllo sul contenuto del sito web collegato e non possiamo essere ritenuti responsabili per le informazioni presenti e per il funzionamento del sito web collegato. Tuttavia, elimineremo il link al sito web collegato non appena verremo a conoscenza della presenza di contenuti illegali o di altro tipo sul sito web collegato. Gli operatori del sito web collegato potrebbero raccogliere informazioni da voi che saranno utilizzate in conformità con la loro informativa sulla privacy, che potrebbe differire dalla nostra. Non controlliamo l'impostazione dei cookie sul sito web collegato, pertanto vi consigliamo di consultare i siti web collegati per ulteriori informazioni sui loro cookie e su come gestirli.",
        ],
      },
      {
        heading: 'Sicurezza',
        description: [
          "Ci impegniamo a garantire la sicurezza del sito web e a fornire un'interfaccia cliente priva di frodi. Tutte le informazioni scambiate vengono crittografate tramite software SSL e abbiamo fatto tutto il possibile per garantire che i tuoi dati personali siano protetti da accessi non autorizzati.",
        ],
      },
    ],
  },
  termsAndConditions: {
    title: 'Termini e Condizioni',
    content: [
      {
        heading: 'Termini e condizioni del sito web',
        paragraphs: [
          "Questo sito web è gestito per conto di Beko Italy S.r.l. da Domestic & General Group Limited.  La visualizzazione o l'utilizzo di questo sito web implica l'accettazione delle seguenti condizioni d'uso.  Se non si accettano queste condizioni d'uso e non si desidera essere vincolati da esse, è necessario interrompere l'utilizzo di questo sito web e uscirne immediatamente.",
        ],
      },
      {
        heading: '1. Definizioni',
        paragraphs: [
          '" D&G " significa Servizi Domestic & General Italia S.r.l. Servizi Domestic & General Italia S.r.l. con unico socio, via Piero Gobetti 2/C, 20063 Cernusco sul Naviglio (MI). Numero di iscrizione al. Registro delle Imprese di Milano e Codice Fiscale 06416700968 - R.E.A. n.1891622, Milano.',
          '" Cliente " indica Beko Italy S.r.l.',
          '" Noi " significa Servizi Domestic & General Italia S.r.l.',
          '" Sito Web" indica il sito di iscrizione e garanzia del Cliente, disponibile all\'indirizzo< URL TBC> ed è di proprietà di D&G.',
          '" Voi" indica l\'utente di questo sito web.',
        ],
      },
      {
        heading: 'Informazioni sulla società',
        paragraphs: [
          'Presidente del Consiglio di sorveglianza: David Tyler',
          'Consiglio di amministrazione: Joe Fitzgerald and Steve Purser',
          'Codice Fiscale: 06416700968',
          'Partita IVA: 06416700968',
          'Sede legale: Milano, Italia',
        ],
      },
      {
        heading: 'Utilizzo del sito web',
        paragraphs: [
          'Siete autorizzati a consultare questo sito web e a riprodurne degli estratti stampandoli o scaricandoli su un disco rigido solo per uso personale. Non è consentito:',
          'Copiare, distribuire, modificare, alterare in qualsiasi modo o utilizzare in altro modo qualsiasi contenuto di questo sito, ad eccezione di quanto consentito sopra.  Copiare, distribuire o utilizzare in altro modo qualsiasi contenuto di questo sito per scopi commerciali.',
          'Rimuovere qualsiasi copyright, marchio di fabbrica o altri avvisi di proprietà originariamente contenuti nei contenuti copiati o stampati dal sito web.',
          "Si prega di notare che tutti i diritti d'autore e di proprietà intellettuale relativi al contenuto di questo sito web sono di proprietà di D&G. Il sito web contiene marchi, tra cui il nome e il logo del Cliente, che sono di proprietà del Cliente, e il nome e il logo di D&G, che sono di proprietà di D&G. Nessun logo o immagine può essere ritrasmesso o copiato, in tutto o in parte, senza l'espressa autorizzazione del suo proprietario. Per ulteriori informazioni sulle modalità di utilizzo dei dati personali raccolti su questo sito web, si rimanda alla Dichiarazione sulla sicurezza dei dati e sulla privacy presente su questo sito.",
        ],
      },
      {
        heading: 'Contenuto',
        paragraphs: [
          "Tutte le informazioni contenute in questo sito sono soggette a modifiche senza preavviso. Non ci impegniamo a mantenere sempre aggiornate le informazioni. Abbiamo adottato misure ragionevoli per verificare l'accuratezza e la pertinenza del contenuto di questo sito, ma non forniamo alcuna garanzia o dichiarazione - espressa o implicita - in merito al contenuto di questo sito, compresi la commerciabilità, l'idoneità per uno scopo particolare, il titolo e la legalità. Tutte le informazioni sono fornite \"così come sono\" e non possiamo essere ritenuti responsabili di eventuali perdite o danni derivanti dall'uso delle informazioni o dei consigli contenuti in questo sito; né possiamo essere ritenuti responsabili di eventuali errori od omissioni nel contenuto di questo sito. Inoltre, non possiamo garantire che il sito sia disponibile senza interruzioni e non ci assumiamo alcuna responsabilità per eventuali perdite o danni derivanti da tale indisponibilità.",
        ],
      },
      {
        heading: 'Link a siti esterni',
        paragraphs: [
          'Questo sito può contenere collegamenti a siti esterni. Questi link sono forniti per aiutarvi a trovare ulteriori informazioni in modo rapido e semplice. Cliccando su un link, potreste lasciare il nostro sito e i contenuti controllati da D&G ed essere reindirizzati a un sito esterno di terzi ("Sito collegato"). D&G non ha alcuna influenza sul contenuto del sito collegato e non è responsabile delle informazioni fornite sul sito collegato. Non è ragionevolmente possibile per D&G monitorare costantemente il contenuto dei Siti Collegati in assenza di indicazioni specifiche di violazione. Tuttavia, D&G rimuoverà il link a un sito nel caso in cui venga a conoscenza di contenuti illegali o che violano in altro modo il sito collegato. D&G non si assume alcuna responsabilità per il contenuto o l\'uso di tali siti collegati.',
        ],
      },
      {
        heading: 'Chi siamo',
        paragraphs: [
          'Il sito web è gestito per conto del Cliente da D&G.',
          'Servizi di registrazione dei prodotti',
          'Servizi Domestic & General Italia S.r.l fornisce servizi di registrazione dei prodotti per conto del Cliente.',
        ],
      },
      {
        heading: 'Informazioni importanti sul fornitore',
        paragraphs: [
          'Servizi Domestic & General Italia S.r.l. con unico socio, via Piero Gobetti 2/C, 20063 Cernusco sul Naviglio (MI). Capitale sociale € 10.000,00 (interamente versato) - Numero di iscrizione al Registro delle Imprese di Milano e Codice Fiscale 06416700968 - R.E.A. n. 1891622, Milano.',
        ],
      },
      {
        heading: 'Responsabilità',
        paragraphs: [
          "Non ci assumiamo alcuna responsabilità per eventuali guasti, malfunzionamenti o indisponibilità di questo sito web, a meno che ciò non derivi da inadempienza intenzionale, frode o grave negligenza da parte nostra. Non ci assumiamo alcuna responsabilità per eventuali danni o perdite speciali, indiretti o accidentali subiti dall'utente (compresa la perdita di profitti, opportunità o avviamento) a causa dell'uso di questo sito web o del suo malfunzionamento. Nulla in questi termini e condizioni esclude o limita la nostra responsabilità per morte o lesioni personali derivanti da nostra negligenza. Possiamo sospendere, ritirare, limitare o cancellare l'uso di questo sito web in qualsiasi momento.",
        ],
      },
      {
        heading: 'Legge applicabile',
        paragraphs: [
          "I presenti termini e condizioni e l'utilizzo del sito web sono regolati dalla legge italiana.",
        ],
      },
      {
        heading: 'Limiti territoriali',
        paragraphs: [
          "Il sito è attualmente destinato a persone che lo visualizzano e lo utilizzano dall'Italia.",
        ],
      },
      {
        heading: 'I virus',
        paragraphs: [
          "Adottiamo misure ragionevoli per garantire che questo sito sia privo di virus. Tuttavia, non possiamo essere ritenuti responsabili per eventuali danni causati da virus o altre tecnologie derivanti dall'uso di questo sito o dal download del suo contenuto o del contenuto di qualsiasi sito collegato.",
        ],
      },
      {
        heading: 'Consigli e raccomandazioni',
        paragraphs: [
          'Nulla di quanto contenuto in questo sito web deve essere considerato un consiglio o una raccomandazione. Quando decidete se acquistare o meno una polizza assicurativa su questo sito web, non dovete fare affidamento esclusivamente sulle informazioni contenute nelle sue pagine. Si consiglia di leggere attentamente la documentazione relativa alla polizza e/o i termini e le condizioni specifici di un determinato prodotto prima di prendere una decisione di acquisto.',
        ],
      },
      {
        heading: 'Contattateci',
        paragraphs: [
          "Se avete dubbi su questo sito web o desiderate contattarci, potete farlo inviando un'e-mail all'indirizzo servizio.clienti@domesticandgeneral.com o chiamando il nostro Servizio clienti al numero 800 894 064.",
          'Grazie per aver visitato questo sito. Si prega di notare che i termini e le condizioni di cui sopra possono essere modificati da D&G di volta in volta, pertanto si consiglia di controllarli ogni volta che si visita questo sito.',
        ],
      },
    ],
  },
};
